import { supabase } from '../lib/supabase'
import { attachCaseTriageDetailsToAppointmentRows } from './supportCenterCaseTriage'
import { supportCenterFormsService } from './supportCenterForms'
import { peopleContactsService } from './peopleContacts'
import { nowTimestampSaoPaulo, timestampDayEnd, timestampDayStart, ymdInSaoPaulo } from '../utils/dateOnly'
import {
  buildStrategicMetrics,
  emptyStrategicMetrics,
  type StrategicAppointmentRow,
} from '../utils/supportCenterStrategicMetrics'
import { isSupportCenterFormKind } from '../types/supportCenterSpecialties'
import type {
  SupportCenterAppointmentStatus,
  SupportCenterFormKind,
  SupportCenterTriageStatus,
} from '../types/supportCenterTriage'
import type {
  SupportCenterAppointmentRecord,
  SupportCenterHistoryItem,
  SupportCenterAppointmentListFilters,
  SupportCenterTriageStatusFilter,
  SupportCenterVolumeByCatalog,
  SupportCenterVolumeByChannel,
  SupportCenterVolumeByDay,
  SupportCenterVolumeByKinship,
  SupportCenterVolumeBySpecialization,
  SupportCenterVolumeBySpecializationCatalog,
  SupportCenterVolumeFilters,
  SupportCenterVolumeMetrics,
} from '../types/supportCenterReports'
import {
  SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS,
  type SupportCenterAppointmentChannel,
} from '../types/supportCenterAppointments'

const QUERY_PAGE_SIZE = 1000
const IN_QUERY_CHUNK_SIZE = 500

async function fetchAllPaged<T>(
  runPage: (
    from: number,
    to: number
  ) => PromiseLike<{ data: T[] | null; error: unknown }>
): Promise<T[]> {
  const all: T[] = []
  let from = 0
  for (;;) {
    const { data, error } = await runPage(from, from + QUERY_PAGE_SIZE - 1)
    if (error) throw error
    const batch = data ?? []
    all.push(...batch)
    if (batch.length < QUERY_PAGE_SIZE) break
    from += QUERY_PAGE_SIZE
  }
  return all
}

function chunkArray<T>(items: T[], size: number = IN_QUERY_CHUNK_SIZE): T[][] {
  if (items.length === 0) return []
  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function coverageCodeFilter(value?: string): number | null {
  const digits = onlyDigits(value ?? '')
  if (!digits) return null
  const n = Number(digits)
  return Number.isSafeInteger(n) ? n : null
}

function coverageCodeList(values?: string[]): number[] {
  if (!values?.length) return []
  const nums: number[] = []
  const seen = new Set<number>()
  for (const value of values) {
    const n = coverageCodeFilter(value)
    if (n == null || seen.has(n)) continue
    seen.add(n)
    nums.push(n)
  }
  return nums
}

function sortNumericLabels(values: Iterable<string>): string[] {
  return [...values].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
}

function matchesAppointmentStatusFilter(
  status: SupportCenterAppointmentStatus,
  filter?: SupportCenterAppointmentStatus[]
): boolean {
  if (!filter || filter.length === 0) return true
  return filter.includes(status)
}

function matchesTriageStatusFilter(
  triageStatus: SupportCenterTriageStatus | null,
  filter?: SupportCenterTriageStatusFilter[]
): boolean {
  if (!filter || filter.length === 0) return true
  if (!triageStatus) return filter.includes('none')
  return filter.includes(triageStatus)
}

function personName(raw: unknown): string | null {
  const person = Array.isArray(raw) ? raw[0] : raw
  if (!person || typeof person !== 'object') return null
  const name = (person as { name?: string | null }).name
  return typeof name === 'string' && name.trim() ? name.trim() : null
}

function matchesPatientQuery(
  row: {
    holder_cpf?: string | null
    patient_cpf?: string | null
    holder_name?: string | null
    patient_name?: string | null
  },
  query?: string
): boolean {
  const patientQuery = query?.trim()
  if (!patientQuery) return true
  const digits = onlyDigits(patientQuery)
  const nameNeedle = patientQuery.toLowerCase()
  const cpfMatch =
    digits.length >= 3 &&
    (onlyDigits(row.patient_cpf ?? '').includes(digits) ||
      onlyDigits(row.holder_cpf ?? '').includes(digits))
  const nameMatch =
    (row.patient_name ?? '').toLowerCase().includes(nameNeedle) ||
    (row.holder_name ?? '').toLowerCase().includes(nameNeedle)
  return cpfMatch || nameMatch
}

function resolveRowTriageStatus(row: Record<string, unknown>): SupportCenterTriageStatus | null {
  const triageRaw = row.triage
  const triage = Array.isArray(triageRaw) ? triageRaw[0] : triageRaw
  if (!triage || typeof triage !== 'object') return null
  const t = triage as Record<string, unknown>
  if (t.deleted_at) return null
  const tStatus = String(t.status ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  const normalized =
    tStatus === 'reprovada' ? 'reprovado' : tStatus === 'concluida' ? 'concluida' : tStatus
  return normalized &&
    normalized in { pendente: 1, concluida: 1, reprovado: 1, cancelado: 1, nao_realizado: 1 }
    ? (normalized as SupportCenterTriageStatus)
    : null
}

function resolveActiveTriage(row: Record<string, unknown>): Record<string, unknown> | null {
  const triageRaw = row.triage
  const triage = Array.isArray(triageRaw) ? triageRaw[0] : triageRaw
  if (!triage || typeof triage !== 'object') return null
  const t = triage as Record<string, unknown>
  if (t.deleted_at) return null
  return t
}

function triageDemandTypeIds(triage: Record<string, unknown> | null): string[] {
  if (!triage) return []
  const raw = triage.triage_demand_types
  if (!Array.isArray(raw)) return []
  const ids: string[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const id = String((item as Record<string, unknown>).demand_type_id ?? '').trim()
    if (id) ids.push(id)
  }
  return ids
}

function firstNestedObject(raw: unknown): Record<string, unknown> | null {
  const value = Array.isArray(raw) ? raw[0] : raw
  if (!value || typeof value !== 'object') return null
  return value as Record<string, unknown>
}

function triageReferralCatalogItem(
  triage: Record<string, unknown>
): { id: string; name: string } {
  const origin = firstNestedObject(triage.referral_origin)
  const id = String(origin?.id ?? triage.referral_origin_id ?? '').trim()
  const name = String(origin?.name ?? '').trim()
  if (id && name) return { id, name }
  if (id) return { id, name: '—' }
  return { id: '__none__', name: 'Não informado' }
}

function triageDemandCatalogItems(
  triage: Record<string, unknown>
): Array<{ id: string; name: string }> {
  const raw = triage.triage_demand_types
  if (!Array.isArray(raw) || raw.length === 0) {
    return [{ id: '__none__', name: 'Não informado' }]
  }
  const items: Array<{ id: string; name: string }> = []
  const seen = new Set<string>()
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue
    const entry = row as Record<string, unknown>
    const demand = firstNestedObject(entry.demand_type)
    const id = String(demand?.id ?? entry.demand_type_id ?? '').trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    const name = String(demand?.name ?? '').trim() || '—'
    items.push({ id, name })
  }
  return items.length > 0 ? items : [{ id: '__none__', name: 'Não informado' }]
}

function bumpCatalogCount(
  map: Map<string, SupportCenterVolumeByCatalog>,
  item: { id: string; name: string }
): void {
  const existing = map.get(item.id)
  if (existing) {
    existing.total += 1
    return
  }
  map.set(item.id, { id: item.id, name: item.name, total: 1 })
}

function sortCatalogVolumeRows(
  rows: SupportCenterVolumeByCatalog[]
): SupportCenterVolumeByCatalog[] {
  return [...rows].sort((a, b) => {
    const aNone = a.id === '__none__'
    const bNone = b.id === '__none__'
    if (aNone !== bNone) return aNone ? 1 : -1
    const aOutros = a.name.trim().toLocaleLowerCase('pt-BR') === 'outros'
    const bOutros = b.name.trim().toLocaleLowerCase('pt-BR') === 'outros'
    if (aOutros !== bOutros) return aOutros ? 1 : -1
    if (b.total !== a.total) return b.total - a.total
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  })
}

function matchesReferralOriginFilter(
  triage: Record<string, unknown> | null,
  originIds?: string[]
): boolean {
  if (!originIds || originIds.length === 0) return true
  if (!triage) return false
  const originId = String(triage.referral_origin_id ?? '').trim()
  return Boolean(originId) && originIds.includes(originId)
}

function matchesDemandTypeFilter(
  triage: Record<string, unknown> | null,
  demandTypeIds?: string[]
): boolean {
  if (!demandTypeIds || demandTypeIds.length === 0) return true
  if (!triage) return false
  const selected = new Set(demandTypeIds)
  return triageDemandTypeIds(triage).some((id) => selected.has(id))
}

function asFormKind(value: unknown): SupportCenterFormKind | null {
  if (isSupportCenterFormKind(value)) return value
  return null
}

function attendanceFormKey(row: Record<string, unknown>): string | null {
  const assistance = firstObject(row.assistance)
  return optionalText(assistance?.specific_form_key)
}

function dayKey(iso: string): string {
  return iso.slice(0, 10)
}

const KINSHIP_ORDER = ['Titular', 'Cônjuge', 'Filho', 'Não informado']

function kinshipLabel(row: Record<string, unknown>): string {
  const holder = onlyDigits(String(row.holder_cpf ?? ''))
  const patient = onlyDigits(String(row.patient_cpf ?? ''))
  if (holder && patient && holder === patient) return 'Titular'
  const kinship = String(row.relationship_to_holder ?? '').trim()
  return kinship || 'Não informado'
}

function matchesKinshipFilter(
  row: Record<string, unknown>,
  kinships?: string[]
): boolean {
  if (!kinships || kinships.length === 0) return true
  return kinships.includes(kinshipLabel(row))
}

function sortKinshipRows(
  rows: SupportCenterVolumeByKinship[]
): SupportCenterVolumeByKinship[] {
  return [...rows].sort((a, b) => {
    const ai = KINSHIP_ORDER.indexOf(a.name)
    const bi = KINSHIP_ORDER.indexOf(b.name)
    if (ai === -1 && bi === -1) return b.total - a.total
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

function emptyVolumeMetrics(): SupportCenterVolumeMetrics {
  return {
    total: 0,
    by_status: {
      marcado: 0,
      reagendado: 0,
      cancelado: 0,
      realizado: 0,
      nao_realizado: 0,
      pre_agendado: 0,
    },
    cancel_rate: 0,
    by_specialization: [],
    by_day: [],
    by_kinship: [],
    by_channel: (
      ['whatsapp', 'telefone', 'videochamada', 'whatsapp-bot'] as SupportCenterAppointmentChannel[]
    ).map((channel) => ({
      channel,
      name: SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS[channel],
      total: 0,
    })),
    attendance_by_demand_type: [],
    attendance_by_demand_type_by_specialization: [],
    triage_by_status: {
      pendente: 0,
      concluida: 0,
      reprovado: 0,
      cancelado: 0,
      nao_realizado: 0,
    },
    triage_by_specialization: [],
    triage_by_day: [],
    triage_by_referral_origin: [],
    triage_by_demand_type: [],
    triage_by_demand_type_by_specialization: [],
    strategic: emptyStrategicMetrics(),
  }
}

function primaryEmail(emails: unknown): string | null {
  if (!Array.isArray(emails)) return null
  const found = emails.map(String).find((e) => e.trim().length > 0)
  return found?.trim() || null
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

function firstObject(raw: unknown): Record<string, unknown> | null {
  const value = Array.isArray(raw) ? raw[0] : raw
  if (!value || typeof value !== 'object') return null
  return value as Record<string, unknown>
}

function optionalText(value: unknown): string | null {
  if (value == null) return null
  const text = String(value).trim()
  return text || null
}

function optionalNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function asUuidArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map(String).map((item) => item.trim()).filter(Boolean)
}

function mapAppointmentListRow(
  raw: Record<string, unknown>,
  contactsById?: Map<string, { value: string }>
): SupportCenterHistoryItem {
  const specRaw = raw.specialization
  const spec = Array.isArray(specRaw) ? specRaw[0] : specRaw
  const specObj = spec && typeof spec === 'object' ? (spec as Record<string, unknown>) : null

  const holderRaw = raw.holder_people
  const holder = Array.isArray(holderRaw) ? holderRaw[0] : holderRaw
  const holderObj = holder && typeof holder === 'object' ? (holder as Record<string, unknown>) : null

  const patientRaw = raw.patient_people
  const patient = Array.isArray(patientRaw) ? patientRaw[0] : patientRaw
  const patientObj =
    patient && typeof patient === 'object' ? (patient as Record<string, unknown>) : null

  const phoneContactId = (raw.phone_contact_id as string | null) ?? null
  const emailContactIds = asUuidArray(raw.email_contact_ids)
  const phoneContactRaw = raw.phone_contact
  const phoneContact = Array.isArray(phoneContactRaw) ? phoneContactRaw[0] : phoneContactRaw
  const phoneFromJoin =
    phoneContact && typeof phoneContact === 'object'
      ? String((phoneContact as { value?: string }).value ?? '').trim() || null
      : null
  const phoneFromMap = phoneContactId
    ? contactsById?.get(phoneContactId)?.value?.trim() || null
    : null
  const emailsFromMap = emailContactIds
    .map((id) => contactsById?.get(id)?.value?.trim() || '')
    .filter(Boolean)

  let status = (raw.status as SupportCenterAppointmentStatus) || 'marcado'
  if (
    raw.deleted_at &&
    (status === 'marcado' || status === 'reagendado' || status === 'pre_agendado')
  ) {
    status = 'cancelado'
  }

  return {
    appointment_id: String(raw.id),
    scheduled_at: String(raw.scheduled_at ?? ''),
    status,
    holder_cpf: String(raw.holder_cpf ?? ''),
    patient_cpf: String(raw.patient_cpf ?? ''),
    patient_phone: phoneFromMap ?? phoneFromJoin,
    client_email: primaryEmail(emailsFromMap),
    holder_name: (holderObj?.name as string | null) ?? null,
    patient_name: (patientObj?.name as string | null) ?? null,
    relationship_to_holder:
      String(raw.relationship_to_holder ?? raw.relacionamento_com_titular ?? '').trim() || null,
    specialization_id: String(raw.specialty_id ?? raw.professional_id ?? raw.specialization_id ?? ''),
    specialization_name: (specObj?.name as string | null) ?? null,
    form_kind: asFormKind(firstObject(raw.assistance)?.specific_form_key),
    triage_id: (raw.triage_id as string | null) ?? null,
    triage_status: resolveRowTriageStatus(raw),
    attendant_email: primaryEmail(raw.internal_emails),
    attendant_name: null,
  }
}

async function fetchProfileNamesByIds(userIds: string[]): Promise<Map<string, string>> {
  const unique = [...new Set(userIds.map((id) => id.trim()).filter(Boolean))]
  const map = new Map<string, string>()
  if (unique.length === 0) return map

  const { data, error } = await supabase.from('profiles').select('id, name').in('id', unique)
  if (error) throw error

  for (const row of data ?? []) {
    const id = String(row.id ?? '').trim()
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    if (id && name) map.set(id, name)
  }
  return map
}

const REPORTS_LIST_TRIAGE_SELECT = `
  id,
  status,
  deleted_at,
  referral_origin_id,
  triage_demand_types:support_center_triage_demand_types(demand_type_id)
`

const REPORTS_VOLUME_TRIAGE_SELECT = `
  id,
  status,
  deleted_at,
  created_at,
  triaged_at,
  referral_origin_id,
  referral_origin:support_center_referral_origins!referral_origin_id(id, name),
  triage_demand_types:support_center_triage_demand_types(
    demand_type_id,
    demand_type:support_center_demand_types!demand_type_id(id, name)
  )
`

async function fetchSubmittedAttendanceAppointmentIds(
  rows: Record<string, unknown>[]
): Promise<Set<string>> {
  const idsByFormKey = new Map<string, string[]>()
  for (const row of rows) {
    const appointmentId = String(row.id ?? '').trim()
    const formKey = attendanceFormKey(row)
    if (!appointmentId || !formKey) continue
    const current = idsByFormKey.get(formKey) ?? []
    current.push(appointmentId)
    idsByFormKey.set(formKey, current)
  }

  const tableNamesByKey = await supportCenterFormsService.resolveTableNamesByKeys([...idsByFormKey.keys()])
  const submittedIds = new Set<string>()

  for (const [formKey, rawIds] of idsByFormKey) {
    const table = tableNamesByKey.get(formKey)?.table_name
    if (!table) continue
    const uniqueIds = [...new Set(rawIds)]
    for (const ids of chunkArray(uniqueIds)) {
      const { data, error } = await supabase.from(table).select('id, submitted_at').in('id', ids)
      if (error) {
        if (error.code === '42P01' || /does not exist/i.test(error.message)) {
          continue
        }
        throw error
      }
      for (const item of data ?? []) {
        const id = String((item as { id?: string }).id ?? '').trim()
        const submittedAt = (item as { submitted_at?: string | null }).submitted_at
        if (id && submittedAt) submittedIds.add(id)
      }
    }
  }

  return submittedIds
}

async function fetchAttendanceDemandCatalogItemsByAppointmentIds(
  appointmentIds: string[]
): Promise<Map<string, Array<{ id: string; name: string }>>> {
  const uniqueIds = [...new Set(appointmentIds.map((id) => id.trim()).filter(Boolean))]
  const byAppointment = new Map<string, Array<{ id: string; name: string }>>()
  if (uniqueIds.length === 0) return byAppointment

  for (const ids of chunkArray(uniqueIds)) {
    const { data, error } = await supabase
      .from('support_center_attendance_demand_types')
      .select(
        `
        appointment_id,
        demand_type_id,
        demand_type:support_center_demand_types!demand_type_id(id, name)
      `
      )
      .in('appointment_id', ids)

    if (error) {
      if (error.code === '42P01' || /does not exist/i.test(error.message)) {
        continue
      }
      throw error
    }

    for (const row of data ?? []) {
      const entry = row as Record<string, unknown>
      const appointmentId = String(entry.appointment_id ?? '').trim()
      if (!appointmentId) continue
      const demand = firstObject(entry.demand_type)
      const demandId = String(demand?.id ?? entry.demand_type_id ?? '').trim()
      if (!demandId) continue
      const demandName = String(demand?.name ?? '').trim() || '—'
      const current = byAppointment.get(appointmentId) ?? []
      if (!current.some((item) => item.id === demandId)) {
        current.push({ id: demandId, name: demandName })
        byAppointment.set(appointmentId, current)
      }
    }
  }

  return byAppointment
}

async function mapAppointmentListRowsFromEnriched(
  withTriage: Record<string, unknown>[]
): Promise<SupportCenterHistoryItem[]> {
  const ids: string[] = []
  for (const raw of withTriage) {
    const phoneId = String(raw.phone_contact_id ?? '').trim()
    if (phoneId) ids.push(phoneId)
    for (const emailId of asUuidArray(raw.email_contact_ids)) ids.push(emailId)
  }
  const contactsById = await peopleContactsService.getByIds(ids)
  const mapped = withTriage.map((raw) => mapAppointmentListRow(raw, contactsById))
  const namesById = await fetchProfileNamesByIds(
    withTriage.map((raw) => String(raw.professional_user_id ?? ''))
  )

  return mapped.map((item, index) => {
    const professionalId = String(withTriage[index]?.professional_user_id ?? '').trim()
    const name = professionalId ? namesById.get(professionalId) ?? null : null
    return name ? { ...item, attendant_name: name } : item
  })
}

async function mapAppointmentListRows(
  rows: Record<string, unknown>[]
): Promise<SupportCenterHistoryItem[]> {
  const withTriage = await attachCaseTriageDetailsToAppointmentRows(
    rows,
    REPORTS_LIST_TRIAGE_SELECT
  )
  return mapAppointmentListRowsFromEnriched(withTriage)
}

const APPOINTMENT_LIST_SELECT = `
  id,
  scheduled_at,
  status,
  deleted_at,
  holder_cpf,
  patient_cpf,
  relationship_to_holder,
  phone_contact_id,
  email_contact_ids,
  internal_emails,
  specialty_id,
  assistance_id,
  professional_user_id,
  specialization:support_center_specialties!specialty_id(id, name),
  assistance:support_center_assistances!assistance_id(id, specific_form_key),
  phone_contact:people_contacts!phone_contact_id(id, value, contact_type),
  holder_people:people!holder_cpf(id, name, tax_id_number),
  patient_people:people!patient_cpf(id, name, tax_id_number)
`

function mapStrategicRow(
  row: Record<string, unknown>,
  status: SupportCenterAppointmentStatus,
  submittedIds: Set<string>
): StrategicAppointmentRow {
  const assistance = firstObject(row.assistance)
  const patient = firstObject(row.patient_people)
  const professional = firstObject(row.professional)
  const triage = resolveActiveTriage(row)
  const professionalUserId = optionalText(row.professional_user_id) ?? ''
  const appointmentId = String(row.id ?? '').trim()
  return {
    id: appointmentId,
    status,
    scheduledAt: String(row.scheduled_at ?? ''),
    deletedAt: optionalText(row.deleted_at),
    patientCpf: String(row.patient_cpf ?? ''),
    assistanceId: optionalText(row.assistance_id) ?? '',
    assistanceName: optionalText(assistance?.name) ?? '',
    professionalUserId,
    professionalName: optionalText(professional?.name) ?? '',
    policyCode: optionalNumber(row.policy_code) != null ? String(optionalNumber(row.policy_code)) : null,
    agreementCode:
      optionalNumber(row.agreement_code) != null ? String(optionalNumber(row.agreement_code)) : null,
    confirmedAt: optionalText(row.confirmed_at),
    attendanceStartedAt: optionalText(row.attendance_started_at),
    attendanceCompletedAt: optionalText(row.attendance_completed_at),
    cancellationChannel: optionalText(row.cancellation_channel),
    cancelReasonName: optionalText(firstObject(row.cancel_reason)?.name),
    birthDate: optionalText(patient?.captured_birth_date),
    triageId: triage ? optionalText(triage.id) : null,
    triageStatus: resolveRowTriageStatus(row),
    triageCreatedAt: optionalText(triage?.created_at),
    triagedAt: optionalText(triage?.triaged_at),
    formKey: attendanceFormKey(row),
    formSubmitted: submittedIds.has(appointmentId),
  }
}

/** Indicadores visíveis na aba: profissional, faixa etária, reagendamento e cancelamento. */
async function computeStrategicMetrics(
  source: Array<{ row: Record<string, unknown>; status: SupportCenterAppointmentStatus }>,
  submittedIds: Set<string>
): Promise<ReturnType<typeof buildStrategicMetrics>> {
  const rows = source.map((item) => mapStrategicRow(item.row, item.status, submittedIds))
  return buildStrategicMetrics({
    rows,
    nowWall: nowTimestampSaoPaulo(),
    todayYmd: ymdInSaoPaulo(),
  })
}

export const supportCenterReportsService = {
  /** Valores distintos já gravados nos agendamentos, para os filtros do dashboard. */
  async listPolicyAndConvenioOptions(): Promise<{
    policyNumbers: string[]
    convenioCodes: string[]
  }> {
    const rows = await fetchAllPaged<{
      policy_code: number | string | null
      agreement_code: number | string | null
    }>((from, to) =>
      supabase
        .from('support_center_appointments')
        .select('policy_code, agreement_code')
        .order('id', { ascending: true })
        .range(from, to)
    )

    const policies = new Set<string>()
    const convenios = new Set<string>()
    for (const row of rows) {
      if (row.policy_code != null && String(row.policy_code).trim()) {
        policies.add(String(row.policy_code).trim())
      }
      if (row.agreement_code != null && String(row.agreement_code).trim()) {
        convenios.add(String(row.agreement_code).trim())
      }
    }

    return {
      policyNumbers: sortNumericLabels(policies),
      convenioCodes: sortNumericLabels(convenios),
    }
  },

  /**
   * Busca clientes por código SCGP ou nome — filtro dashboard.
   * Query vazia: lista até 50 (abrir sem digitar). Com texto: filtra.
   */
  async searchClients(query: string): Promise<
    Array<{ id: string; name: string; id_scgp: number | null }>
  > {
    const q = query.trim()
    let dbQuery = supabase
      .from('clients')
      .select('id, name, id_scgp')
      .is('deleted_at', null)
      .order('name')
      .limit(50)

    if (q) {
      const num = /^\d+$/.test(q) ? parseInt(q, 10) : NaN
      if (!Number.isNaN(num)) {
        dbQuery = dbQuery.or(`name.ilike.%${q}%,id_scgp.eq.${num}`)
      } else {
        dbQuery = dbQuery.ilike('name', `%${q}%`)
      }
    }

    const { data, error } = await dbQuery
    if (error) throw error
    return (data ?? []).map((row) => ({
      id: String(row.id),
      name: typeof row.name === 'string' ? row.name : '',
      id_scgp:
        typeof row.id_scgp === 'number'
          ? row.id_scgp
          : row.id_scgp != null && Number.isFinite(Number(row.id_scgp))
            ? Number(row.id_scgp)
            : null,
    }))
  },

  /**
   * Corretores e clientes que já aparecem em agendamentos (broker_code / client_code = id_scgp).
   * Usado nos filtros do dashboard para não listar o catálogo inteiro.
   */
  async listBrokerAndClientFilterOptions(): Promise<{
    brokers: Array<{ id: string; name: string; id_scgp: number | null }>
    clients: Array<{ id: string; name: string; id_scgp: number | null }>
  }> {
    const rows = await fetchAllPaged<{
      broker_code: number | string | null
      client_code: number | string | null
    }>((from, to) =>
      supabase
        .from('support_center_appointments')
        .select('broker_code, client_code')
        .order('id', { ascending: true })
        .range(from, to)
    )

    const brokerCodes = new Set<number>()
    const clientCodes = new Set<number>()
    for (const row of rows) {
      const broker = coverageCodeFilter(
        row.broker_code != null ? String(row.broker_code) : ''
      )
      // 0 = placeholder NOT NULL sem código real
      if (broker != null && broker !== 0) brokerCodes.add(broker)
      const client = coverageCodeFilter(
        row.client_code != null ? String(row.client_code) : ''
      )
      if (client != null && client !== 0) clientCodes.add(client)
    }

    const brokerList = [...brokerCodes]
    const clientList = [...clientCodes]
    const brokersByCode = new Map<number, { id: string; name: string; id_scgp: number }>()
    const clientsByCode = new Map<number, { id: string; name: string; id_scgp: number }>()

    if (brokerList.length > 0) {
      const { data, error } = await supabase
        .from('brokers')
        .select('id, name, id_scgp')
        .in('id_scgp', brokerList)
      if (error) throw error
      for (const row of data ?? []) {
        const code = coverageCodeFilter(
          row.id_scgp != null ? String(row.id_scgp) : ''
        )
        if (code == null) continue
        brokersByCode.set(code, {
          id: String(row.id),
          name: typeof row.name === 'string' ? row.name.trim() : '',
          id_scgp: code,
        })
      }
    }

    if (clientList.length > 0) {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, id_scgp')
        .in('id_scgp', clientList)
        .is('deleted_at', null)
      if (error) throw error
      for (const row of data ?? []) {
        const code = coverageCodeFilter(
          row.id_scgp != null ? String(row.id_scgp) : ''
        )
        if (code == null) continue
        clientsByCode.set(code, {
          id: String(row.id),
          name: typeof row.name === 'string' ? row.name.trim() : '',
          id_scgp: code,
        })
      }
    }

    const brokers = sortNumericLabels(brokerList.map(String)).map((value) => {
      const code = Number(value)
      const found = brokersByCode.get(code)
      return (
        found ?? {
          id: `scgp-broker-${code}`,
          name: value,
          id_scgp: code,
        }
      )
    })

    const clients = sortNumericLabels(clientList.map(String)).map((value) => {
      const code = Number(value)
      const found = clientsByCode.get(code)
      return (
        found ?? {
          id: `scgp-client-${code}`,
          name: value,
          id_scgp: code,
        }
      )
    })

    return { brokers, clients }
  },

  async getAppointmentRecord(id: string): Promise<SupportCenterAppointmentRecord | null> {
    const appointmentId = id.trim()
    if (!appointmentId) return null

    const { data, error } = await supabase
      .from('support_center_appointments')
      .select(
        `
        *,
        specialization:support_center_specialties!specialty_id(id, name),
        assistance:support_center_assistances!assistance_id(id, name, code, specific_form_key),
        cancel_reason:support_center_cancel_reasons!cancel_reason_id(id, name),
        phone_contact:people_contacts!phone_contact_id(id, value),
        holder_people:people!holder_cpf(id, name),
        patient_people:people!patient_cpf(id, name)
      `
      )
      .eq('id', appointmentId)
      .maybeSingle()

    if (error) throw error
    if (!data) return null

    const [withTriage] = await attachCaseTriageDetailsToAppointmentRows([
      data as unknown as Record<string, unknown>,
    ])
    const raw = withTriage
    const spec = firstObject(raw.specialization)
    const assistance = firstObject(raw.assistance)
    const cancelReason = firstObject(raw.cancel_reason)
    const phoneContact = firstObject(raw.phone_contact)
    const holder = firstObject(raw.holder_people)
    const patient = firstObject(raw.patient_people)
    const phoneContactId = optionalText(raw.phone_contact_id)
    const emailContactIds = asUuidArray(raw.email_contact_ids)
    const contactIds = [...(phoneContactId ? [phoneContactId] : []), ...emailContactIds]
    const contactsById = await peopleContactsService.getByIds(contactIds)
    const phone =
      (phoneContactId ? contactsById.get(phoneContactId)?.value?.trim() : '') ||
      optionalText(phoneContact?.value)
    const clientEmails = emailContactIds
      .map((contactId) => contactsById.get(contactId)?.value?.trim() || '')
      .filter(Boolean)

    const professionalUserId = optionalText(raw.professional_user_id)
    const deletedBy = optionalText(raw.deleted_by)
    const namesById = await fetchProfileNamesByIds(
      [professionalUserId, deletedBy].filter((value): value is string => Boolean(value))
    )

    const status = (optionalText(raw.status) || 'marcado') as SupportCenterAppointmentStatus

    return {
      id: String(raw.id),
      scheduled_at: String(raw.scheduled_at ?? ''),
      status,
      channel: optionalText(raw.channel),
      holder_cpf: String(raw.holder_cpf ?? ''),
      holder_name: optionalText(holder?.name),
      patient_cpf: String(raw.patient_cpf ?? ''),
      patient_name: optionalText(patient?.name),
      relationship_to_holder: optionalText(raw.relationship_to_holder),
      specialty_id: String(raw.specialty_id ?? raw.specialization_id ?? ''),
      specialization_name: optionalText(spec?.name),
      assistance_id: optionalText(raw.assistance_id),
      assistance_name: optionalText(assistance?.name),
      assistance_code: optionalText(assistance?.code),
      form_kind: asFormKind(assistance?.specific_form_key),
      professional_user_id: professionalUserId,
      professional_name: professionalUserId ? namesById.get(professionalUserId) ?? null : null,
      internal_emails: asUuidArray(raw.internal_emails),
      phone_contact_id: phoneContactId,
      patient_phone: phone || null,
      email_contact_ids: emailContactIds,
      client_emails: clientEmails,
      call_link: optionalText(raw.call_link),
      call_link_short: optionalText(raw.call_link_short),
      outlook_event_id: optionalText(raw.outlook_event_id),
      triage_id: optionalText(raw.triage_id),
      triage_status: resolveRowTriageStatus(raw),
      confirmed_at: optionalText(raw.confirmed_at),
      confirmation_requested_at: optionalText(raw.confirmation_requested_at),
      attendance_started_at: optionalText(raw.attendance_started_at),
      attendance_completed_at: optionalText(raw.attendance_completed_at),
      policy_code: optionalNumber(raw.policy_code),
      agreement_code: optionalNumber(raw.agreement_code),
      assistance_provider_code: optionalNumber(raw.assistance_provider_code),
      cancel_reason_id: optionalText(raw.cancel_reason_id),
      cancel_reason_name: optionalText(cancelReason?.name),
      cancellation_channel: optionalText(raw.cancellation_channel),
      deleted_at: optionalText(raw.deleted_at),
      deleted_by: deletedBy,
      deleted_by_name: deletedBy ? namesById.get(deletedBy) ?? null : null,
      created_at: String(raw.created_at ?? ''),
      updated_at: String(raw.updated_at ?? ''),
    }
  },

  async getVolumeMetrics(filters: SupportCenterVolumeFilters = {}): Promise<SupportCenterVolumeMetrics> {
    // specialization_ids vazio = escopo amplo (sem filtro); com ids = restringe.
    let query = supabase
      .from('support_center_appointments')
      .select(
        `
        id,
        status,
        deleted_at,
        scheduled_at,
        specialty_id,
        assistance_id,
        professional_user_id,
        channel,
        holder_cpf,
        patient_cpf,
        relationship_to_holder,
        confirmed_at,
        attendance_started_at,
        attendance_completed_at,
        policy_code,
        agreement_code,
        cancellation_channel,
        assistance:support_center_assistances!assistance_id(id, name, specific_form_key),
        specialization:support_center_specialties!specialty_id(id, name),
        cancel_reason:support_center_cancel_reasons!cancel_reason_id(id, name),
        professional:profiles!professional_user_id(id, name),
        holder_people:people!holder_cpf(id, name, tax_id_number),
        patient_people:people!patient_cpf(id, name, tax_id_number, captured_birth_date)
      `
      )
      .order('scheduled_at', { ascending: true })
      .order('id', { ascending: true })

    if (filters.date_from) {
      query = query.gte('scheduled_at', timestampDayStart(filters.date_from))
    }
    if (filters.date_to) {
      query = query.lte('scheduled_at', timestampDayEnd(filters.date_to))
    }
    if (filters.specialization_ids && filters.specialization_ids.length > 0) {
      query = query.in('specialty_id', filters.specialization_ids)
    }
    if (filters.specialization_id) {
      query = query.eq('specialty_id', filters.specialization_id)
    }
    if (filters.professional_user_ids && filters.professional_user_ids.length > 0) {
      query = query.in('professional_user_id', filters.professional_user_ids)
    } else if (filters.professional_user_id) {
      query = query.eq('professional_user_id', filters.professional_user_id)
    }
    if (filters.attendant_email?.trim()) {
      query = query.contains('internal_emails', [normalizeEmail(filters.attendant_email)])
    }
    if (filters.channels && filters.channels.length > 0) {
      query = query.in('channel', filters.channels)
    }
    const policyNumbers = coverageCodeList(filters.policy_numbers)
    if (policyNumbers.length > 0) {
      query = query.in('policy_code', policyNumbers)
    }
    const convenioCodes = coverageCodeList(filters.convenio_codes)
    if (convenioCodes.length > 0) {
      query = query.in('agreement_code', convenioCodes)
    }
    if (filters.assistance_ids && filters.assistance_ids.length > 0) {
      query = query.in('assistance_id', filters.assistance_ids)
    }
    const brokerCodes = coverageCodeList(filters.broker_codes)
    if (brokerCodes.length > 0) {
      query = query.in('broker_code', brokerCodes)
    }
    const clientCodes = coverageCodeList(filters.client_codes)
    if (clientCodes.length > 0) {
      query = query.in('client_code', clientCodes)
    }

    const rawAppointmentRows = await fetchAllPaged<Record<string, unknown>>((from, to) =>
      query.range(from, to)
    )
    if (rawAppointmentRows.length === 0) return emptyVolumeMetrics()

    const rows = await attachCaseTriageDetailsToAppointmentRows(
      rawAppointmentRows,
      REPORTS_VOLUME_TRIAGE_SELECT
    )

    const metrics = emptyVolumeMetrics()
    const openStatuses = new Set<SupportCenterAppointmentStatus>([
      'marcado',
      'reagendado',
      'pre_agendado',
    ])
    const bySpec = new Map<string, SupportCenterVolumeBySpecialization>()
    const byDay = new Map<string, number>()
    const byKinship = new Map<string, number>()
    const byChannel = new Map<SupportCenterAppointmentChannel, number>([
      ['whatsapp', 0],
      ['telefone', 0],
      ['videochamada', 0],
      ['whatsapp-bot', 0],
    ])
    const attendanceByDemand = new Map<string, SupportCenterVolumeByCatalog>()
    const attendanceByDemandBySpec = new Map<
      string,
      { name: string; demands: Map<string, SupportCenterVolumeByCatalog> }
    >()
    const triageBySpec = new Map<string, SupportCenterVolumeBySpecialization>()
    const triageByDay = new Map<string, number>()
    const triageByReferral = new Map<string, SupportCenterVolumeByCatalog>()
    const triageByDemand = new Map<string, SupportCenterVolumeByCatalog>()
    const triageByDemandBySpec = new Map<
      string,
      { name: string; demands: Map<string, SupportCenterVolumeByCatalog> }
    >()
    const submittedAttendanceIds = await fetchSubmittedAttendanceAppointmentIds(rows)
    const attendanceDemandsByAppointment = await fetchAttendanceDemandCatalogItemsByAppointmentIds(
      [...submittedAttendanceIds]
    )
    const strategicSource: Array<{
      row: Record<string, unknown>
      status: SupportCenterAppointmentStatus
    }> = []

    for (const raw of rows) {
      const row = raw as Record<string, unknown>
      // Soft-delete incompleto (deleted_at + marcado/reagendado) → cancelado.
      // nao_realizado/realizado podem ter deleted_at e devem entrar na volumetria.
      let status = (row.status as SupportCenterAppointmentStatus) || 'marcado'
      if (row.deleted_at && openStatuses.has(status)) {
        status = 'cancelado'
      }
      const triage = resolveActiveTriage(row)
      const triageStatus = resolveRowTriageStatus(row)
      if (!matchesAppointmentStatusFilter(status, filters.appointment_statuses)) continue
      if (!matchesTriageStatusFilter(triageStatus, filters.triage_statuses)) continue
      if (!matchesReferralOriginFilter(triage, filters.referral_origin_ids)) continue
      if (!matchesDemandTypeFilter(triage, filters.demand_type_ids)) continue
      if (!matchesKinshipFilter(row, filters.kinships)) continue
      if (
        !matchesPatientQuery(
          {
            holder_cpf: String(row.holder_cpf ?? ''),
            patient_cpf: String(row.patient_cpf ?? ''),
            holder_name: personName(row.holder_people),
            patient_name: personName(row.patient_people),
          },
          filters.patient_query
        )
      ) {
        continue
      }

      strategicSource.push({ row, status })

      // Pré-agendamento não entra na volumetria de atendimento.
      if (status === 'pre_agendado') continue

      if (status in metrics.by_status) {
        metrics.by_status[status] += 1
      }
      metrics.total += 1

      const scheduledAt = String(row.scheduled_at ?? '')
      if (scheduledAt) {
        const key = dayKey(scheduledAt)
        byDay.set(key, (byDay.get(key) ?? 0) + 1)
      }

      const kinship = kinshipLabel(row)
      byKinship.set(kinship, (byKinship.get(kinship) ?? 0) + 1)

      const channelRaw = String(row.channel ?? '').trim()
      const channel: SupportCenterAppointmentChannel =
        channelRaw === 'whatsapp' ||
        channelRaw === 'telefone' ||
        channelRaw === 'videochamada' ||
        channelRaw === 'whatsapp-bot'
          ? channelRaw
          : 'whatsapp-bot'
      byChannel.set(channel, (byChannel.get(channel) ?? 0) + 1)

      const specRaw = row.specialization
      const spec = Array.isArray(specRaw) ? specRaw[0] : specRaw
      const specObj =
        spec && typeof spec === 'object' ? (spec as Record<string, unknown>) : null
      const specId = String(
        row.specialty_id ?? row.professional_id ?? row.specialization_id ?? specObj?.id ?? ''
      )
      const specName = String(specObj?.name ?? '—')

      const appointmentId = String(row.id ?? '').trim()
      if (submittedAttendanceIds.has(appointmentId)) {
        for (const demand of attendanceDemandsByAppointment.get(appointmentId) ?? []) {
          bumpCatalogCount(attendanceByDemand, demand)
          if (specId) {
            let specBucket = attendanceByDemandBySpec.get(specId)
            if (!specBucket) {
              specBucket = { name: specName, demands: new Map() }
              attendanceByDemandBySpec.set(specId, specBucket)
            }
            bumpCatalogCount(specBucket.demands, demand)
          }
        }
      }

      if (specId) {
        const existing = bySpec.get(specId)
        if (existing) {
          existing.total += 1
        } else {
          bySpec.set(specId, {
            specialization_id: specId,
            name: specName,
            total: 1,
          })
        }
      }

      if (triageStatus && triageStatus in metrics.triage_by_status) {
        metrics.triage_by_status[triageStatus] += 1
        if (scheduledAt) {
          const triageDayKey = dayKey(scheduledAt)
          triageByDay.set(triageDayKey, (triageByDay.get(triageDayKey) ?? 0) + 1)
        }
        if (specId) {
          const existingTriageSpec = triageBySpec.get(specId)
          if (existingTriageSpec) {
            existingTriageSpec.total += 1
          } else {
            triageBySpec.set(specId, {
              specialization_id: specId,
              name: specName,
              total: 1,
            })
          }
        }
        if (triage) {
          bumpCatalogCount(triageByReferral, triageReferralCatalogItem(triage))
          for (const demand of triageDemandCatalogItems(triage)) {
            bumpCatalogCount(triageByDemand, demand)
            if (specId) {
              let specBucket = triageByDemandBySpec.get(specId)
              if (!specBucket) {
                specBucket = { name: specName, demands: new Map() }
                triageByDemandBySpec.set(specId, specBucket)
              }
              bumpCatalogCount(specBucket.demands, demand)
            }
          }
        }
      }
    }

    metrics.cancel_rate =
      metrics.total > 0
        ? Math.round((metrics.by_status.cancelado / metrics.total) * 1000) / 10
        : 0

    metrics.by_specialization = [...bySpec.values()].sort((a, b) => b.total - a.total)
    metrics.by_day = [...byDay.entries()]
      .map(([date, total]) => ({ date, total }) satisfies SupportCenterVolumeByDay)
      .sort((a, b) => a.date.localeCompare(b.date))
    metrics.by_kinship = sortKinshipRows(
      [...byKinship.entries()].map(([name, total]) => ({ name, total }))
    )
    const channelOrder: SupportCenterAppointmentChannel[] = [
      'whatsapp',
      'telefone',
      'videochamada',
      'whatsapp-bot',
    ]
    metrics.by_channel = channelOrder.map(
      (channel) =>
        ({
          channel,
          name: SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS[channel],
          total: byChannel.get(channel) ?? 0,
        }) satisfies SupportCenterVolumeByChannel
    )
    metrics.triage_by_specialization = [...triageBySpec.values()].sort((a, b) => b.total - a.total)
    metrics.triage_by_day = [...triageByDay.entries()]
      .map(([date, total]) => ({ date, total }) satisfies SupportCenterVolumeByDay)
      .sort((a, b) => a.date.localeCompare(b.date))
    metrics.attendance_by_demand_type = sortCatalogVolumeRows([...attendanceByDemand.values()])
    metrics.attendance_by_demand_type_by_specialization = [
      ...attendanceByDemandBySpec.entries(),
    ]
      .map(
        ([specialization_id, bucket]) =>
          ({
            specialization_id,
            name: bucket.name,
            items: sortCatalogVolumeRows([...bucket.demands.values()]),
          }) satisfies SupportCenterVolumeBySpecializationCatalog
      )
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
    metrics.triage_by_referral_origin = sortCatalogVolumeRows([...triageByReferral.values()])
    metrics.triage_by_demand_type = sortCatalogVolumeRows([...triageByDemand.values()])
    metrics.triage_by_demand_type_by_specialization = [...triageByDemandBySpec.entries()]
      .map(
        ([specialization_id, bucket]) =>
          ({
            specialization_id,
            name: bucket.name,
            items: sortCatalogVolumeRows([...bucket.demands.values()]),
          }) satisfies SupportCenterVolumeBySpecializationCatalog
      )
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))

    metrics.strategic = await computeStrategicMetrics(strategicSource, submittedAttendanceIds)
    return metrics
  },

  async listAppointments(
    filters: SupportCenterAppointmentListFilters = {}
  ): Promise<SupportCenterHistoryItem[]> {
    // specialization_ids vazio = escopo amplo (sem filtro); com ids = restringe.
    let query = supabase
      .from('support_center_appointments')
      .select(APPOINTMENT_LIST_SELECT)
      .order('scheduled_at', { ascending: false })
      .order('id', { ascending: true })

    if (filters.date_from) {
      query = query.gte('scheduled_at', timestampDayStart(filters.date_from))
    }
    if (filters.date_to) {
      query = query.lte('scheduled_at', timestampDayEnd(filters.date_to))
    }
    if (filters.specialization_ids && filters.specialization_ids.length > 0) {
      query = query.in('specialty_id', filters.specialization_ids)
    }
    if (filters.specialization_id) {
      query = query.eq('specialty_id', filters.specialization_id)
    }
    if (filters.professional_user_ids && filters.professional_user_ids.length > 0) {
      query = query.in('professional_user_id', filters.professional_user_ids)
    } else if (filters.professional_user_id) {
      query = query.eq('professional_user_id', filters.professional_user_id)
    }
    if (filters.attendant_email?.trim()) {
      query = query.contains('internal_emails', [normalizeEmail(filters.attendant_email)])
    }
    const policyNumbers = coverageCodeList(filters.policy_numbers)
    if (policyNumbers.length > 0) {
      query = query.in('policy_code', policyNumbers)
    }
    const convenioCodes = coverageCodeList(filters.convenio_codes)
    if (convenioCodes.length > 0) {
      query = query.in('agreement_code', convenioCodes)
    }
    if (filters.assistance_ids && filters.assistance_ids.length > 0) {
      query = query.in('assistance_id', filters.assistance_ids)
    }
    if (filters.channels && filters.channels.length > 0) {
      query = query.in('channel', filters.channels)
    }
    const brokerCodes = coverageCodeList(filters.broker_codes)
    if (brokerCodes.length > 0) {
      query = query.in('broker_code', brokerCodes)
    }
    const clientCodes = coverageCodeList(filters.client_codes)
    if (clientCodes.length > 0) {
      query = query.in('client_code', clientCodes)
    }

    const rawRows = await fetchAllPaged<Record<string, unknown>>((from, to) =>
      query.range(from, to)
    )
    const withTriage = await attachCaseTriageDetailsToAppointmentRows(
      rawRows,
      REPORTS_LIST_TRIAGE_SELECT
    )

    const needsTriageCatalogFilter =
      (filters.referral_origin_ids && filters.referral_origin_ids.length > 0) ||
      (filters.demand_type_ids && filters.demand_type_ids.length > 0)

    const filteredRaw = needsTriageCatalogFilter
      ? withTriage.filter((row) => {
          const triage = resolveActiveTriage(row)
          return (
            matchesReferralOriginFilter(triage, filters.referral_origin_ids) &&
            matchesDemandTypeFilter(triage, filters.demand_type_ids)
          )
        })
      : withTriage

    let rows = await mapAppointmentListRowsFromEnriched(filteredRaw)

    if (filters.appointment_statuses && filters.appointment_statuses.length > 0) {
      rows = rows.filter((row) =>
        matchesAppointmentStatusFilter(row.status, filters.appointment_statuses)
      )
    }
    if (filters.triage_statuses && filters.triage_statuses.length > 0) {
      rows = rows.filter((row) =>
        matchesTriageStatusFilter(row.triage_status, filters.triage_statuses)
      )
    }

    const patientQuery = filters.patient_query?.trim()
    if (patientQuery) {
      rows = rows.filter((row) => matchesPatientQuery(row, patientQuery))
    }

    if (filters.kinships && filters.kinships.length > 0) {
      rows = rows.filter((row) =>
        matchesKinshipFilter(
          {
            holder_cpf: row.holder_cpf,
            patient_cpf: row.patient_cpf,
            relationship_to_holder: row.relationship_to_holder,
          },
          filters.kinships
        )
      )
    }

    return rows
  },

  /**
   * Histórico geral de agendamentos do paciente (todas as especialidades / formulários).
   */
  async listPatientHistory(input: {
    patient_cpf: string
  }): Promise<SupportCenterHistoryItem[]> {
    const digits = onlyDigits(input.patient_cpf)
    if (digits.length !== 11) {
      throw new Error('Informe o CPF do paciente com 11 dígitos para ver o histórico.')
    }

    const { data, error } = await supabase
      .from('support_center_appointments')
      .select(APPOINTMENT_LIST_SELECT)
      .eq('patient_cpf', digits)
      .order('scheduled_at', { ascending: false })

    if (error) throw error

    return mapAppointmentListRows((data ?? []) as unknown as Record<string, unknown>[])
  },

  /**
   * Histórico de agendamentos do paciente filtrado pelo tipo de formulário
   * (psicológico / nutricional / fitness / jurídico / assistência social).
   */
  async listPatientHistoryByFormKind(input: {
    patient_cpf: string
    form_kind: SupportCenterFormKind
    /** Inclui a consulta neste instante e as anteriores; exclui posteriores. */
    until_scheduled_at?: string
  }): Promise<SupportCenterHistoryItem[]> {
    const digits = onlyDigits(input.patient_cpf)
    if (digits.length !== 11) {
      throw new Error('Informe o CPF do paciente com 11 dígitos para ver o histórico.')
    }

    let query = supabase
      .from('support_center_appointments')
      .select(
        `
        id,
        scheduled_at,
        status,
        deleted_at,
        holder_cpf,
        patient_cpf,
        relationship_to_holder,
        phone_contact_id,
        email_contact_ids,
        internal_emails,
        specialty_id,
        assistance_id,
        professional_user_id,
        specialization:support_center_specialties!specialty_id(id, name),
        assistance:support_center_assistances!assistance_id!inner(id, specific_form_key),
        phone_contact:people_contacts!phone_contact_id(id, value, contact_type),
        holder_people:people!holder_cpf(id, name, tax_id_number),
        patient_people:people!patient_cpf(id, name, tax_id_number)
      `
      )
      .eq('patient_cpf', digits)
      .eq('assistance.specific_form_key', input.form_kind)

    const until = input.until_scheduled_at?.trim()
    if (until) {
      query = query.lte('scheduled_at', until)
    }

    const { data, error } = await query.order('scheduled_at', { ascending: false })

    if (error) throw error

    return mapAppointmentListRows((data ?? []) as unknown as Record<string, unknown>[])
  },
}
